import React, { useEffect, useState } from 'react';
import { Row, Col, Tabs, Descriptions, Tag } from 'antd';
import useGlobal from '../../../store/Store';
import { Link } from 'react-router-dom';
import { ClassificationResult } from '../../../common/results/ClassificationResult';
import { ClassificationResult as ClassificationResultType, InferenceFile } from '../../../types';
import { Label } from '../../../types/label';
import { Flow, MultiflowIteratorResult, MultiflowStatus, MultiflowValidationResult } from '../../../types/flow';
import axios from 'axios';
import { FlowPath } from '../../../components/FlowPath';
import { ResultFields } from './ResultFields';
import { ResultTables } from '../../../common/results/ResultTables';
import { ResultProperties } from '../../../common/results/ResultProperties';
import { CustomSider } from '../../../components/layout/CustomSider';
import { Section } from '../../../components/Section';
import { getFlow } from '../../../lib/api/flow-api';
import { ResultTablesGeneric } from '../../../common/results/ResultTablesGeneric';

const { TabPane } = Tabs;

type Props = { inferenceFile: null | InferenceFile; multiflowStatus: null | MultiflowStatus };

const ResultSideBar = ({ inferenceFile, multiflowStatus }: Props) => {
  const [, globalActions] = useGlobal();
  const [hoveredLabel, setHoveredLabel] = useState<null | Label>(null);
  const [multiValidationResult, setMultiValidationResult] = useState<null | MultiflowValidationResult>(null);
  const [multiIteratorResult, setMultiIteratorResult] = useState<null | MultiflowIteratorResult>(null);
  const [flow, setFlow] = useState<Flow | null>(null);

  useEffect(() => {
    const fetchMultiValidation = async () => {
      if (multiflowStatus && inferenceFile) {
        const { data } = await axios(multiflowStatus.validationResultUrl);
        const validationResult: MultiflowValidationResult = data;
        setMultiValidationResult(validationResult);
      }
    };
    const fetchMultiIterator = async () => {
      if (multiflowStatus && inferenceFile) {
        const { data } = await axios(multiflowStatus.iteratorResultUrl);
        const iteratorResult: MultiflowIteratorResult = data;
        setMultiIteratorResult(iteratorResult);
      }
    };
    const fetchFlow = async () => {
      if (inferenceFile) {
        const flow = await getFlow(inferenceFile.flowId || '');
        flow && setFlow(flow);
      }
    };
    fetchMultiValidation();
    fetchMultiIterator();
    fetchFlow();
  }, [inferenceFile, multiflowStatus]);

  const onLabelValueMouseEnter = (label: Label) => {
    setHoveredLabel(label);
    globalActions.setLabelSelected(label);
  };

  const onLabelValueMouseLeave = () => {
    setHoveredLabel(null);
    globalActions.setLabelSelected(null);
  };

  const isMultiflow = () => {
    return (
      inferenceFile && inferenceFile.model && inferenceFile.model.algorithm && inferenceFile.model.algorithm.multiflow
    );
  };

  const getClassification = () => {
    return inferenceFile && inferenceFile.annotations && inferenceFile.annotations.type;
  };

  return (
    <CustomSider width={500} border="left" style={{ paddingTop: '1.25rem' }}>
      <Row>
        {!isMultiflow() && getClassification() ? (
          <ClassificationResult
            readOnly
            result={getClassification()}
            onClassChange={() => {}}
            classes={
              (inferenceFile &&
                inferenceFile.model &&
                inferenceFile.model.info &&
                inferenceFile.model.info.annotationTypes) ||
              []
            }
          />
        ) : (
          <Col span={24}>
            <Section>
              <Descriptions layout="horizontal" column={1}>
                <Descriptions.Item label="Flow">{flow?.name}</Descriptions.Item>
                <Descriptions.Item label="Model">
                  {flow?.multiflowName ? flow.multiflowName : flow?.model.name}
                </Descriptions.Item>
                {isMultiflow() && inferenceFile?.inferenceResult?.result.type && (
                  <Descriptions.Item label="Classes">
                    {(inferenceFile?.inferenceResult?.result.type as ClassificationResultType[]).map(type => (
                      <Tag key={type.name}>{type.name}</Tag>
                    ))}
                  </Descriptions.Item>
                )}
              </Descriptions>
              {!isMultiflow() && (
                <Link
                  className="ant-btn ant-btn-secondary ant-btn-md mr-4"
                  to={{ pathname: inferenceFile?.presignedUrl }}
                  target="_blank"
                  download>
                  Download Pdf
                </Link>
              )}
            </Section>
            {isMultiflow() && <FlowPath inferenceFile={inferenceFile} multiIteratorResult={multiIteratorResult} />}
            {isMultiflow() || inferenceFile?.model.algorithm.id === 1 ? (
              <Tabs
                style={{ marginTop: '20px' }}
                size="large"
                tabBarStyle={{ padding: '0 20px', color: 'rgba(0, 94, 160, 1)', fontWeight: 'bold' }}>
                <TabPane tab="Fields" key="1">
                  <ResultFields
                    hoveredLabel={hoveredLabel}
                    onLabelValueMouseEnter={onLabelValueMouseEnter}
                    onLabelValueMouseLeave={onLabelValueMouseLeave}
                    isMultiflow={isMultiflow}
                    multiValidationResult={multiValidationResult}
                    inTable={true}
                  />
                </TabPane>
                <TabPane tab="Tables" key="2">
                  {flow?.multiflowName === 'invoice' ? (
                    <ResultTables inferenceFile={inferenceFile} />
                  ) : (
                    <ResultTablesGeneric
                      inferenceFile={inferenceFile}
                      onLabelValueMouseEnter={onLabelValueMouseEnter}
                      onLabelValueMouseLeave={onLabelValueMouseLeave}
                      hoveredLabel={hoveredLabel}
                      multiValidationResult={multiValidationResult}
                    />
                  )}
                </TabPane>
                <TabPane tab="Properties" key="3">
                  <ResultProperties inferenceFile={inferenceFile} />
                </TabPane>
              </Tabs>
            ) : (
              <>
                <ResultFields
                  hoveredLabel={hoveredLabel}
                  onLabelValueMouseEnter={onLabelValueMouseEnter}
                  isMultiflow={isMultiflow}
                  onLabelValueMouseLeave={onLabelValueMouseLeave}
                  multiValidationResult={multiValidationResult}
                  inTable={false}
                />
              </>
            )}
          </Col>
        )}
      </Row>
    </CustomSider>
  );
};

export default ResultSideBar;
